/**
 * Stripe subscription management service
 * Handles subscription creation, updates, and billing operations
 */

import { getStripe, STRIPE_PRICE_IDS, SUBSCRIPTION_TIERS, SubscriptionTier } from './config';
import { auth, db } from '../firebase/config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { collection, query, where, limit as qLimit, getDocs, setDoc } from 'firebase/firestore';
import { PRICING_DATA } from '../../utils/constants';

function dbg(..._args: any[]) {
  // Temporary debug logging disabled for production builds
  // Uncomment the line below for local debugging:
  // console.log('[Stripe Subscriptions]', ...args);
}

// Helper to safely convert possible Firestore/Stripe timestamp shapes to Date
function toJsDate(v: any): Date {
  try {
    if (!v) return new Date(0);
    if (typeof (v as any).toDate === 'function') return (v as any).toDate();
    if (typeof v === 'number') return new Date(v < 2e10 ? v * 1000 : v);
    if (typeof v === 'string') {
      const n = Number(v);
      if (!Number.isNaN(n)) return new Date(n < 2e10 ? n * 1000 : n);
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}
  return new Date(0);
}

/**
 * Subscription data interface
 */
export interface SubscriptionData {
  id: string;
  customerId: string;
  priceId: string;
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  tier: SubscriptionTier;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Billing details interface
 */
export interface BillingDetails {
  name: string;
  email: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

/**
 * Create Stripe checkout session for subscription
 */
export const createCheckoutSession = async (
  priceId: string,
  tier: SubscriptionTier,
  billingDetails?: BillingDetails
): Promise<{ sessionId: string; url: string }> => {
  const user = auth.currentUser;
  
  // Allow guest checkout for 12-month package (payment-first flow)
  const isGuestCheckout = tier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE && !user;
  
  if (!user && !isGuestCheckout) {
    dbg('createCheckoutSession: no authenticated user');
    throw new Error('User must be authenticated to create subscription');
  }

  try {
    const idToken = user ? await user.getIdToken() : undefined;
    const payload = {
      priceId,
      tier,
      userId: user?.uid,
      userEmail: user?.email || undefined,
      billingDetails,
      successUrl: `${window.location.origin}${isGuestCheckout ? '/complete-registration' : '/dashboard/billing'}?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}${isGuestCheckout ? '/twelve-month-package' : '/pricing'}`,
    };

    // Debug inputs before request
    // eslint-disable-next-line no-console
    console.log('createCheckoutSession payload:', {
      priceId,
      tier,
      userId: user?.uid,
      hasEmail: !!user?.email,
      successUrl: payload.successUrl,
      cancelUrl: payload.cancelUrl,
      priceIdLooksValid: /^price_/.test(priceId),
      isGuestCheckout,
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Only add Authorization header if user is authenticated
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Attempt to read response body for diagnostics
      let bodyText = '';
      try { bodyText = await response.text(); } catch (_) {}
      console.error('createCheckoutSession failed:', {
        status: response.status,
        statusText: response.statusText,
        body: bodyText,
        priceId,
        tier,
      });

      // Try to surface a clearer message if JSON error is present
      try {
        const maybeJson = JSON.parse(bodyText);
        if (maybeJson && maybeJson.error) {
          throw new Error(`Failed to create checkout session: ${maybeJson.error}`);
        }
      } catch (_) {
        // not JSON, fallthrough
      }

      throw new Error(`Failed to create checkout session (${response.status}): ${bodyText || response.statusText}`);
    }

    const data = await response.json();
    dbg('createCheckoutSession success:', { sessionId: data.sessionId, hasUrl: !!data.url });
    return {
      sessionId: data.sessionId,
      url: data.url,
    };
  } catch (error) {
    dbg('createCheckoutSession exception:', error);
    throw error;
  }
};

/**
 * Redirect to Stripe checkout
 */
export const redirectToCheckout = async (
  priceId: string,
  tier: SubscriptionTier,
  billingDetails?: BillingDetails
): Promise<void> => {
  try {
    dbg('redirectToCheckout called with:', { priceId, tier });
    const stripe = await getStripe();
    if (!stripe) {
      dbg('Stripe not available. Check publishable key.');
      throw new Error('Stripe is not available');
    }

    const { sessionId } = await createCheckoutSession(priceId, tier, billingDetails);
    dbg('redirecting with sessionId:', sessionId);

    const { error } = await stripe.redirectToCheckout({ sessionId });

    if (error) {
      dbg('redirectToCheckout error:', error);
      throw error;
    }
  } catch (error) {
    dbg('redirectToCheckout exception:', error);
    throw error;
  }
};

// Confirm a checkout session (fallback if webhooks are delayed)
export const confirmCheckoutSession = async (sessionId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  try {
    const res = await fetch('/api/stripe/confirm-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({ userId: user.uid, sessionId }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      dbg('confirmCheckoutSession failed:', res.status, txt);
      throw new Error('Failed to confirm session');
    }
    dbg('confirmCheckoutSession success');
  } catch (e) {
    dbg('confirmCheckoutSession exception:', e);
    throw e;
  }
};

// Sync the latest Stripe subscription into Firestore (use after upgrades/portal changes)
export const syncSubscription = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  try {
    const res = await fetch('/api/stripe/sync-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({ userId: user.uid }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      dbg('syncSubscription failed:', res.status, txt);
      throw new Error('Failed to sync subscription');
    }
    dbg('syncSubscription success');
  } catch (e) {
    dbg('syncSubscription exception:', e);
    throw e;
  }
};

// Preview upgrade cost (immediate charge/proration) for existing subscribers
export const previewUpgrade = async (
  newPriceId: string
): Promise<{ amountDue: number; currency: string; lines: Array<{ amount: number; currency: string; description?: string; proration: boolean }>; nextPaymentAttempt?: number | null; }> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  try {
    const res = await fetch('/api/stripe/preview-upgrade', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({ userId: user.uid, newPriceId }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      dbg('previewUpgrade failed:', res.status, txt);
      throw new Error('Failed to preview upgrade');
    }
    const data = await res.json();
    const result = {
      amountDue: data.amount_due || 0,
      currency: (data.currency || 'usd').toUpperCase(),
      lines: (data.lines || []).map((l: any) => ({ amount: l.amount || 0, currency: (l.currency || 'usd').toUpperCase(), description: l.description, proration: !!l.proration })),
      nextPaymentAttempt: data.next_payment_attempt ?? null,
    } as const;
    dbg('previewUpgrade success:', result);
    return result as any;
  } catch (e) {
    dbg('previewUpgrade exception:', e);
    throw e;
  }
};

export const scheduleDowngrade = async (newPriceId: string, newTier: SubscriptionTier): Promise<{ effectiveDate: number }> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  const res = await fetch('/api/stripe/schedule-downgrade', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
    },
    body: JSON.stringify({ userId: user.uid, newPriceId, newTier }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    dbg('scheduleDowngrade failed:', res.status, txt);
    throw new Error('Failed to schedule downgrade');
  }
  const data = await res.json();
  dbg('scheduleDowngrade success:', data);
  return { effectiveDate: data.effectiveDate };
};

// Cancel a scheduled downgrade (removes Stripe schedule and clears flags)
export const cancelScheduledDowngrade = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  const res = await fetch('/api/stripe/cancel-downgrade', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
    },
    body: JSON.stringify({ userId: user.uid }),
  });
  if (!res.ok) {
    let msg = 'Failed to cancel scheduled downgrade';
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch (_) {
      const txt = await res.text().catch(() => '');
      if (txt) msg = txt;
      dbg('cancelScheduledDowngrade failed:', res.status, txt);
    }
    throw new Error(msg);
  }
  dbg('cancelScheduledDowngrade success');
};

// Create a Checkout session to pay proration for an upgrade now
export const createUpgradeCheckoutSession = async (
  newPriceId: string,
  newTier: SubscriptionTier
): Promise<{ sessionId: string; url: string }> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  const res = await fetch('/api/stripe/create-upgrade-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
    },
    body: JSON.stringify({
      userId: user.uid,
      newPriceId,
      newTier,
      successUrl: `${window.location.origin}/dashboard/billing?upgrade_session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/dashboard/billing`,
    }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    dbg('createUpgradeCheckoutSession failed:', res.status, txt);
    throw new Error('Failed to create upgrade checkout');
  }
  const data = await res.json();
  dbg('createUpgradeCheckoutSession success:', { sessionId: data.sessionId, hasUrl: !!data.url });
  return { sessionId: data.sessionId, url: data.url };
};

// Confirm an upgrade payment session and switch the plan
export const confirmUpgradeCheckoutSession = async (sessionId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  const res = await fetch('/api/stripe/confirm-upgrade-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`,
    },
    body: JSON.stringify({ userId: user.uid, sessionId }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    dbg('confirmUpgradeCheckoutSession failed:', res.status, txt);
    throw new Error('Failed to confirm upgrade session');
  }
  dbg('confirmUpgradeCheckoutSession success');
};

/**
 * Get user's current subscription
 */
export const getCurrentSubscription = async (): Promise<SubscriptionData | null> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    dbg('getCurrentSubscription: start', { uid: user.uid });
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    const userData: any = userSnap.data() || {};

    if (userData.subscriptionTier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME) {
      const now = new Date();
      const farFuture = new Date(now.getFullYear() + 50, 0, 1);
      return {
        id: `stackcommerce-${user.uid}`,
        customerId: userData.stripeCustomerId || '',
        priceId: 'activation_code',
        status: 'active',
        tier: SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME,
        currentPeriodStart: toJsDate(userData.updatedAt || userData.createdAt || now),
        currentPeriodEnd: farFuture,
        cancelAtPeriodEnd: false,
        trialEnd: undefined,
        createdAt: toJsDate(userData.createdAt || now),
        updatedAt: toJsDate(userData.updatedAt || now),
      };
    }

    // Preferred ids written by webhooks
    let subId: string | undefined = userData.subscriptionId || userData.stripeSubscriptionId;
    dbg('getCurrentSubscription: user data', {
      hasSubscriptionId: !!userData.subscriptionId,
      hasStripeSubscriptionId: !!userData.stripeSubscriptionId,
      subId,
    });

    let subDocSnap: any;
    if (subId) {
      subDocSnap = await getDoc(doc(db, 'subscriptions', subId));
      dbg('getCurrentSubscription: fetched by id', { exists: subDocSnap?.exists?.() });
    }

    // Fallback: query by userId (no orderBy, so no composite index needed)
    if (!subDocSnap || !subDocSnap.exists()) {
      dbg('getCurrentSubscription: fallback query by userId');
      const q = query(collection(db, 'subscriptions'), where('userId', '==', user.uid), qLimit(10));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const docs = snap.docs;
        const parseDate = (v: any) => toJsDate(v).getTime() || 0;
        // Prefer active/trialing with latest period end; else latest updatedAt overall
        const actives = docs.filter(d => {
          const s = (d.data() as any)?.status;
          return s === 'active' || s === 'trialing';
        });
        const pick = (list: typeof docs) => list.reduce((best, d) => {
          if (!best) return d;
          const be = parseDate((best.data() as any)?.currentPeriodEnd || (best.data() as any)?.current_period_end || (best.data() as any)?.updatedAt);
          const de = parseDate((d.data() as any)?.currentPeriodEnd || (d.data() as any)?.current_period_end || (d.data() as any)?.updatedAt);
          return de > be ? d : best;
        }, null as any);
        const preferred = actives.length ? pick(actives) : pick(docs);
        subDocSnap = preferred;
        subId = preferred.id;
        dbg('getCurrentSubscription: fallback picked', { subId, status: preferred.data()?.status, end: (preferred.data() as any)?.currentPeriodEnd });
        // Cache on user for fast access next time
        try { await setDoc(userRef, { subscriptionId: subId }, { merge: true }); } catch (e) { dbg('cache subscriptionId failed', e); }
      } else {
        dbg('getCurrentSubscription: no subscriptions found');
      }
    }

    if (!subDocSnap || !subDocSnap.exists()) return null;

    const d: any = subDocSnap.data();
    const result: SubscriptionData = {
      id: subDocSnap.id,
      customerId: d.customerId,
      priceId: d.priceId,
      status: d.status,
      tier: d.tier || 'free',
      currentPeriodStart: toJsDate(d.currentPeriodStart),
      currentPeriodEnd: toJsDate(d.currentPeriodEnd),
      cancelAtPeriodEnd: !!d.cancelAtPeriodEnd,
      trialEnd: d.trialEnd ? toJsDate(d.trialEnd) : undefined,
      createdAt: toJsDate(d.createdAt),
      updatedAt: toJsDate(d.updatedAt),
    };
    dbg('getCurrentSubscription: returning', {
      id: result.id,
      status: result.status,
      tier: result.tier,
      currentPeriodEnd: result.currentPeriodEnd?.toISOString?.(),
    });
    return result;
  } catch (error) {
    dbg('getCurrentSubscription exception:', error);
    throw error;
  }
};

/**
 * Update subscription tier
 */
export const updateSubscription = async (
  newPriceId: string,
  newTier: SubscriptionTier
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const response = await fetch('/api/stripe/update-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({
        userId: user.uid,
        newPriceId,
        newTier,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update subscription');
    }

    // Ensure Firestore reflects Stripe's latest state immediately
    try {
      await syncSubscription();
    } catch (e) {
      // Non-fatal: UI can still recover on next load
      dbg('updateSubscription: sync after update failed, will rely on webhooks/next refresh', e);
    }

    // Update local user data as a hint for UI
    await updateDoc(doc(db, 'users', user.uid), {
      subscriptionTier: newTier,
      updatedAt: new Date(),
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (cancelAtPeriodEnd: boolean = true): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const response = await fetch('/api/stripe/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({
        userId: user.uid,
        cancelAtPeriodEnd,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    // Update local subscription data
    const subscription = await getCurrentSubscription();
    if (subscription) {
      await updateDoc(doc(db, 'subscriptions', subscription.id), {
        cancelAtPeriodEnd,
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Reactivate canceled subscription
 */
export const reactivateSubscription = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const response = await fetch('/api/stripe/reactivate-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({
        userId: user.uid,
      }),
    });

    if (!response.ok) {
      if (response.status === 409) {
        // Friendly guidance when the sub is fully canceled and cannot be reactivated
        throw new Error('This subscription was fully canceled. Please subscribe again to restart your plan.');
      }
      try {
        const j = await response.json();
        throw new Error(j?.error || 'Failed to reactivate subscription');
      } catch (_) {
        throw new Error('Failed to reactivate subscription');
      }
    }

    // Update local subscription data
    const subscription = await getCurrentSubscription();
    if (subscription) {
      await updateDoc(doc(db, 'subscriptions', subscription.id), {
        cancelAtPeriodEnd: false,
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Create customer portal session
 */
export const createCustomerPortalSession = async (): Promise<{ url: string }> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    const response = await fetch('/api/stripe/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({
        userId: user.uid,
        returnUrl: `${window.location.origin}/dashboard/billing`,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create customer portal session');
    }

    const data = await response.json();
    return { url: data.url };
  } catch (error) {
    throw error;
  }
};

/**
 * Get subscription tier from Stripe price ID
 * Checks all currencies (GBP, EUR, USD) for each tier
 */
export const getTierFromPriceId = (priceId: string): SubscriptionTier => {
  const tierMap: Record<string, SubscriptionTier> = {
    standard: 'standard',
    premium: 'premium',
    twelve_month_package: 'twelve_month_package',
  };

  for (const [tier, currencies] of Object.entries(PRICING_DATA)) {
    if (!(tier in tierMap)) continue;
    for (const currencyData of Object.values(currencies)) {
      if ((currencyData as { priceId: string }).priceId === priceId) {
        return tierMap[tier];
      }
    }
  }
  return 'free';
};

/**
 * Get price ID from subscription tier
 */
export const getPriceIdFromTier = (tier: SubscriptionTier): string => {
  let resolved: string;
  switch (tier) {
    case 'standard':
      resolved = STRIPE_PRICE_IDS.STANDARD; break;
    case 'premium':
      resolved = STRIPE_PRICE_IDS.PREMIUM; break;
    case 'twelve_month_package':
      resolved = STRIPE_PRICE_IDS.TWELVE_MONTH_PACKAGE; break;
    case SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME:
      throw new Error('Stackcommerce lifetime plans are redeemed via activation codes, not Stripe.');
    default:
      throw new Error(`No price ID found for tier: ${tier}`);
  }
  dbg('getPriceIdFromTier:', { tier, priceId: resolved, looksValid: /^price_/.test(resolved) });
  return resolved;
};

/**
 * Subscription service interface
 */
const subscriptionService = {
  createCheckoutSession,
  redirectToCheckout,
  getCurrentSubscription,
  updateSubscription,
  cancelSubscription,
  reactivateSubscription,
  createCustomerPortalSession,
  getTierFromPriceId,
  getPriceIdFromTier,
  confirmCheckoutSession,
  syncSubscription,
  previewUpgrade,
  scheduleDowngrade,
  createUpgradeCheckoutSession,
  confirmUpgradeCheckoutSession,
};

export default subscriptionService;
